import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Users, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const POSITIONS = [
  { value: "ALL", label: "All Positions" },
  { value: "GK", label: "Goalkeepers" },
  { value: "DEF", label: "Defenders" },
  { value: "MID", label: "Midfielders" },
  { value: "FWD", label: "Forwards" },
];

const PlayersDirectoryPage = () => {
  const [players, setPlayers] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("ALL");
  const [teamId, setTeamId] = useState("ALL");

  useEffect(() => {
    (async () => {
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get(`${API}/players`),
          axios.get(`${API}/teams`),
        ]);
        setPlayers(pRes.data || []);
        const map = {};
        (tRes.data || []).forEach((t) => { map[t.team_id] = t; });
        setTeamsMap(map);
      } catch (e) {
        console.error("Players directory load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return players.filter((p) => {
      if (pos !== "ALL" && p.position !== pos) return false;
      if (teamId !== "ALL" && p.team_id !== teamId && !((p.team_ids || []).includes(teamId))) return false;
      if (needle) {
        const hay = `${p.name} ${p.registration_id || ""} ${teamsMap[p.team_id]?.name || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [players, q, pos, teamId, teamsMap]);

  const teamOptions = useMemo(() => {
    return Object.values(teamsMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [teamsMap]);

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="players-directory-page">
      {/* HERO */}
      <section className="bg-[#09090b] text-white py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="inline-block px-3 py-1 bg-[#FFC72C] text-black text-xs font-bold uppercase tracking-widest mb-4">
            Phase 3 — Players
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tight mb-3">
            Every ABFA Player
          </h1>
          <p className="text-gray-400 max-w-2xl">
            Search the full registry of players across the Premier, First and Second Divisions. Each player carries an ABFA Registration ID for lifetime tracking.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="border-b border-gray-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, ABFA ID or club…"
              className="pl-10"
              data-testid="input-player-search"
            />
          </div>
          <select
            value={pos}
            onChange={(e) => setPos(e.target.value)}
            className="border border-gray-200 px-3 py-2 text-sm bg-white uppercase font-semibold"
            data-testid="select-position"
          >
            {POSITIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="border border-gray-200 px-3 py-2 text-sm bg-white font-semibold max-w-[220px]"
            data-testid="select-team"
          >
            <option value="ALL">All Clubs</option>
            {teamOptions.map((t) => (
              <option key={t.team_id} value={t.team_id}>{t.name}</option>
            ))}
          </select>
        </div>
      </section>

      {/* LIST */}
      <section className="py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="h-72" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-heading uppercase">No players match your filters</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4" data-testid="text-result-count">
                Showing <span className="font-bold text-[#09090b]">{filtered.length}</span> of {players.length} players
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filtered.map((p) => {
                  const club = teamsMap[p.team_id];
                  return (
                    <Link
                      key={p.player_id}
                      to={`/players/${p.player_id}`}
                      className="group bg-white border border-gray-100 hover:border-[#FFC72C] transition-all overflow-hidden flex flex-col"
                      data-testid={`player-card-${p.player_id}`}
                    >
                      <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden relative">
                        <img
                          src={p.photo_url || "https://placehold.co/400x500/09090b/FFF?text=PLAYER"}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {p.number != null && (
                          <div className="absolute top-2 right-2 w-10 h-10 bg-[#FFC72C] text-black flex items-center justify-center font-heading text-lg font-black">
                            {p.number}
                          </div>
                        )}
                        {p.registration_id && (
                          <div className="absolute bottom-2 left-2 bg-black/70 text-[#FFC72C] text-[10px] font-mono px-2 py-0.5">
                            {p.registration_id}
                          </div>
                        )}
                      </div>
                      <div className="p-3 flex-1 flex flex-col">
                        <h4 className="font-heading font-bold uppercase text-sm text-[#09090b] group-hover:text-[#009E60] transition-colors line-clamp-1">
                          {p.name}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          {p.position && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.position}</Badge>}
                          {club && <span className="line-clamp-1">{club.name}</span>}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default PlayersDirectoryPage;
